import {
  Grid,
  Skeleton,
  Table as MaUTable,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { isFunction } from 'lodash';
import React, { FC, ReactNode, useEffect, useContext } from 'react';
import {
  Cell,
  ColumnInterface,
  Row,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  UseSortByColumnProps,
  useTable,
  UseTableColumnProps,
  UseTableOptions,
} from 'react-table';

import { formatPercent, getLocalDate, getLocalDateTime } from '../../helpers';
import { Pagination } from './Pagination';
import { HeaderContext } from '../../context';

// `ITableColumn` is our version of `Column`, or rather, `ColumnInterface`
export interface ITableColumn<D extends object = {}> extends ColumnInterface<D> {
  accessor?: ((item: Row<D>) => string | number) | string;
  canFilter?: boolean;
  className?: string;
  columnAlignment?: TableCellProps['align'];
  verticalAlignment?: React.CSSProperties['verticalAlign'];
  filterType?: 'input' | 'autocomplete';
  handleClickColumn?: (columnId: unknown) => void;
  hideLoad?: boolean;
  isCentered?: boolean;
  isCurrency?: boolean;
  isDate?: boolean;
  isDateTime?: boolean;
  isNumber?: boolean;
  isPercent?: boolean;
  isServerSorted?: boolean;
  isServerSortedDesc?: boolean;
  overrideWidth?: number;
  sort?: boolean;
}

export interface ITable<D extends object = {}>
  extends Pick<UseTableOptions<D>, 'data' | 'columns'> {
  cellClasses?: string | ((original: D) => string);
  centerPagination?: boolean;
  containerClasses?: string;
  headerClasses?: string;
  hideDeleted?: boolean;
  hidePagination?: boolean;
  isLoading?: boolean;
  loadingPageSize?: number;
  LoadMoreComponent?: FC;
  mobileProps?: Record<string, unknown>;
  noResultsText?: string;
  onRowsPerPageChange?: (rows: number) => void;
  ResponsiveComponent?: FC;
  ResponsiveComponentLoader?: FC;
  rowClasses?: string | ((original: D) => string);
  rowOnClick?: (val: unknown) => void;
  stickyHeader?: boolean;
  hideTableHeader?: boolean;
  tableSize?: 'medium' | 'small';
  useTableProps?: UseTableOptions<D>;
}

const currencyRE = /\B(?=(\d{3})+(?!\d))/g;

const formatCell = (cell: Cell<{}>): ReactNode => {
  if (cell.value === null || cell.value === '') return;

  const tColumn = cell.column as ITableColumn; // ColumnInstance with extra properties

  if (tColumn.isDate) return getLocalDate(cell.value);
  if (tColumn.isDateTime) return getLocalDateTime(cell.value);
  else if (tColumn.isCurrency) return `$${Number(cell.value).toFixed(2).replace(currencyRE, ',')}`;
  else if (tColumn.isPercent) return formatPercent(cell.value);
  else return cell.render('Cell');
};
const getCellStyle = (column: ITableColumn, type: 'header' | 'cell'): React.CSSProperties => {
  let style: React.CSSProperties = { cursor: 'inherit' };

  if (column.verticalAlignment) {
    style = {
      ...style,
      verticalAlign: column.verticalAlignment,
    };
  }
  if (column.isDate || column.isDateTime) {
    style = {
      ...style,
    };
  } else if (column.isNumber) {
    style = {
      ...style,
      textAlign: 'right',
      paddingRight: '3rem',
    };
  } else if (column.isCurrency) {
    style = {
      ...style,
    };
  } else if (column.isCentered) {
    style = {
      ...style,
      textAlign: 'center',
    };
  }

  if (column.overrideWidth) {
    style = {
      ...style,
      width: column.overrideWidth,
      maxWidth: column.overrideWidth,
      wordWrap: 'break-word', // IE11
      overflowWrap: 'break-word',
    };
  }

  if (type === 'header') {
    style = {
      ...style,
      fontWeight: 'bold',
      cursor: column.handleClickColumn ? 'pointer' : 'inherit',
      color: '#11a5c5',
    };
  }

  return style;
};

export const Table: FC<ITable> = ({
  cellClasses = '',
  centerPagination,
  columns,
  containerClasses = '',
  data,
  headerClasses,
  hideDeleted,
  hidePagination,
  isLoading,
  loadingPageSize,
  LoadMoreComponent,
  mobileProps,
  noResultsText = 'No Results',
  onRowsPerPageChange,
  ResponsiveComponent,
  ResponsiveComponentLoader,
  rowClasses,
  rowOnClick,
  stickyHeader = false,
  hideTableHeader = false,
  tableSize = 'small',
  useTableProps = {},
}) => {
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const { headerHeight } = useContext(HeaderContext);
  const classes = useStyles({ headerHeight });

  const {
    getTableProps,
    headerGroups,
    prepareRow,
    // @ts-ignore
    page,
    // @ts-ignore
    gotoPage,
    // @ts-ignore
    setPageSize,
    // @ts-ignore
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      // @ts-ignore
      userPageCount: 1,
      manualPagination: hidePagination,
      autoResetPage: isLoading,
      autoResetSortBy: isLoading,
      ...useTableProps,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const handleChangePage = (page: number) => {
    gotoPage(page);
  };

  const handleChangeRowsPerPage = (value: number) => {
    setPageSize(value);
    onRowsPerPageChange?.(value);
  };

  // update page size if we hide pagination
  useEffect(() => {
    if (hidePagination) setPageSize(Number(data.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // always start on first page if data length changes, ie; filter was applied
  useEffect(() => {
    handleChangePage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  return (
    <>
      <TableContainer className={clsx(containerClasses, stickyHeader ? classes.stickyHeader : '')}>
        <MaUTable
          stickyHeader={stickyHeader}
          size={tableSize}
          {...getTableProps()}
          style={ResponsiveComponent && !isLoading ? { display: 'block' } : undefined}
        >
          {isDesktop || !ResponsiveComponent ? (
            <>
              {/** TABLE HEADER */}
              {!hideTableHeader ? (
                <TableHead
                  className={clsx(stickyHeader ? classes.stickyTableHeader : '')}
                  sx={{
                    '& tr': {
                      borderRadius: '2px',
                      '& th': {
                        fontWeight: 'bold',
                        backgroundColor: theme => theme.frankie.searchBlue,
                        // Nearly white, tinted towards the themefied Frankie searchBlue color
                        color: '#eff4f6',
                      },
                    },
                  }}
                >
                  {headerGroups.map(headerGroup => (
                    <TableRow {...headerGroup.getHeaderGroupProps()}>
                      {/** TABLE HEADER COLUMNS */}
                      {headerGroup.headers.map((column: any) => (
                        <TableCell
                          align={column?.columnAlignment ?? column?.isCurrency ? 'right' : 'left'}
                          {...(column as UseTableColumnProps<{}>).getHeaderProps(
                            column.sort !== false
                              ? (column as UseSortByColumnProps<{}>).getSortByToggleProps()
                              : undefined
                          )}
                          title=""
                          className={headerClasses}
                          onClick={e => {
                            const { handleClickColumn, sort } = column as ITableColumn;
                            const sortProps = column.getHeaderProps(column.getSortByToggleProps());

                            // If the column is sortable and there's an onClick handler, then call it
                            if (!sort) sortProps?.onClick?.(e);

                            // Also run column click handler if passed
                            handleClickColumn?.(column.id);
                          }}
                        >
                          {(column as ITableColumn).sort !== false ||
                          (column as ITableColumn).isServerSorted ? (
                            <TableSortLabel
                              active={column.isSorted || (column as ITableColumn).isServerSorted}
                              direction={
                                column.isSortedDesc || (column as ITableColumn).isServerSortedDesc
                                  ? 'desc'
                                  : 'asc'
                              }
                            >
                              {column.render('Header')}
                            </TableSortLabel>
                          ) : (
                            column.render('Header')
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
              ) : null}

              {/** TABLE BODY ROWS */}
              <TableBody>
                {isLoading
                  ? new Array(loadingPageSize ?? pageSize ?? 3).fill('').map((_, j) => (
                      <TableRow
                        key={`table-row-skeleton-${j}`}
                        {...(rowOnClick ? { onClick: rowOnClick } : {})}
                      >
                        {/** LOADING SKELETON */}
                        {columns.map((column: any, i) => (
                          <TableCell
                            key={`skeleton-cell-${i}`}
                            style={getCellStyle(column as ITableColumn, 'cell')}
                            className={isFunction(cellClasses) ? cellClasses({}) : cellClasses}
                          >
                            {column.hideLoad ? null : <Skeleton />}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : page.map((row: Row) => {
                      prepareRow(row);
                      return !hideDeleted || (hideDeleted && !(row.original as any).isDeleted) ? (
                        <TableRow
                          {...row.getRowProps()}
                          className={isFunction(rowClasses) ? rowClasses(row.original) : rowClasses}
                          {...(rowOnClick ? { onClick: () => rowOnClick(row.original) } : {})}
                        >
                          {row.cells.map(cell => {
                            const tableColumn: ITableColumn | undefined = cell?.column;
                            return (
                              <TableCell
                                align={
                                  tableColumn?.columnAlignment || tableColumn?.isCurrency
                                    ? 'right'
                                    : 'left'
                                }
                                {...cell.getCellProps()}
                                className={clsx(
                                  tableColumn?.className ?? '',
                                  isFunction(cellClasses) ? cellClasses(row.original) : cellClasses,
                                  tableColumn?.className ?? ''
                                )}
                                style={getCellStyle(tableColumn, 'cell')}
                              >
                                {formatCell(cell)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ) : null;
                    })}
              </TableBody>
            </>
          ) : (
            <TableBody className={isLoading ? undefined : classes.mobileTable}>
              {/** TABLE BODY FROM RESPONSIVE COMPONENT */}
              {isLoading ? (
                <TableRow>
                  <TableCell
                    className={clsx(
                      classes.mobileCell,
                      isFunction(cellClasses) ? cellClasses({}) : cellClasses
                    )}
                  >
                    {ResponsiveComponentLoader ? <ResponsiveComponentLoader /> : <Skeleton />}
                  </TableCell>
                </TableRow>
              ) : (
                (page as Row[]).map((row, i) => {
                  prepareRow(row);
                  return !hideDeleted || (hideDeleted && !(row.original as any).isDeleted) ? (
                    <TableRow
                      {...row.getRowProps()}
                      className={isFunction(rowClasses) ? rowClasses(row.original) : rowClasses}
                      {...(rowOnClick ? { onClick: () => rowOnClick(row.original) } : {})}
                      style={{ display: 'block' }}
                    >
                      <TableCell
                        key={`row-${i}-mobile-cell`}
                        className={clsx(
                          classes.mobileCell,
                          isFunction(cellClasses) ? cellClasses(row.original) : cellClasses
                        )}
                        style={{ display: 'block' }}
                      >
                        <ResponsiveComponent
                          key={`responsive-row-${i}`}
                          {...row}
                          {...useTableProps}
                          {...mobileProps}
                        />
                      </TableCell>
                    </TableRow>
                  ) : null;
                })
              )}
            </TableBody>
          )}
        </MaUTable>

        {/** NO RESULTS DISPLAY */}
        {!isLoading && data.length === 0 && (
          <Grid container justifyContent="center" className={classes.noResults}>
            <Typography variant="body1" gutterBottom>
              {noResultsText}
            </Typography>
          </Grid>
        )}

        {LoadMoreComponent && <LoadMoreComponent />}
      </TableContainer>

      {/** TABLE PAGINATION */}
      {!isLoading && data.length > 0 && !hidePagination && (
        <div className={centerPagination ? classes.centerPagination : ''}>
          <Pagination
            rowsPerPageOptions={[10, 15, 25, 50, { label: 'All', value: data.length }]}
            count={isLoading ? 0 : data.length}
            rowsPerPage={pageSize}
            page={pageIndex}
            setPage={handleChangePage}
            setRowsPerPage={handleChangeRowsPerPage}
          />
        </div>
      )}
    </>
  );
};

const useStyles = makeStyles<Theme, { headerHeight: number }>(theme => ({
  noResults: {
    '& p': {
      marginTop: '1rem',
    },
  },
  stickyHeader: {
    flexGrow: 1,
    maxHeight: '100%',
    overflowX: 'initial',
  },
  stickyTableHeader: {
    position: 'sticky',
    top: ({ headerHeight }) => headerHeight + 'px',
    zIndex: 2,
  },
  borderNone: {
    border: 'none',
  },
  mobileTable: {
    display: 'block',
  },
  mobileCell: {
    padding: 0,
    border: 0,
  },
  centerPagination: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));
